import { _ } from "../../../core/polyglot/polyglot.js";
import { debounce } from "../../../core/helpers/functional-util.js";
import { eventManager } from "@foretagsplatsen/yaem";
import { infoCircleIcon } from "../../../core/elements/icons/index.js";
import { notBlank } from "../../../core/widgets/forms/validators.js";
import { stacker } from "../../../core/elements/index.js";
import AccountingDocumentWidget from "../../shared/documentInfrastructure/AccountingDocumentWidget.js";
import alertBus from "../../../core/alerts/alertBus.js";
import AlertCollector from "../../../core/alerts/AlertCollector.js";
import ForecastAssumptionsWidget from "../../../core/widgets/ForecastAssumptionsWidget/ForecastAssumptionsWidget.js";
import ForecastBalanceSheet from "../../../models/forecast/interpretation/reports/ForecastBalanceSheet.js";
import ForecastCashFlow from "../../../models/forecast/interpretation/reports/ForecastCashFlow.js";
import ForecastIncomeStatement from "../../../models/forecast/interpretation/reports/ForecastIncomeStatement.js";
import ForecastPublicationRequest from "../ForecastPublicationRequest.js";
import ForecastSettingsWidget from "../widgets/ForecastSettingsWidget/ForecastSettingsWidget.js";
import ForecastTabsWidget from "../../../core/widgets/ForecastTabsWidget/ForecastTabsWidget.js";
import regionWidget from "../../../core/widgets/regionWidget.js";
import ValidationResultWidget from "../../../core/widgets/ValidationResultWidget.js";
import WysiwygEditorWidget from "../../../core/widgets/WysiwygEditorWidget/WysiwygEditorWidget.js";

const forecastSettingsTabId = "forecast-settings-tab";
const forecastDescriptionTabId = "forecast-description-tab";

/**
 * Widget rendering the core of the ForecastEditor.
 *
 * @param {FiscalYear[]} fiscalYears - All the company fiscal years
 */
export default class ForecastEditorDocumentContent extends AccountingDocumentWidget {
	constructor({ fiscalYears }) {
		super(...arguments);

		this._fiscalYears = fiscalYears;

		this._forecastHelp = _(
			"Publishing a forecast saves a snapshot of this template as it currently stands. The published forecast will not be modifiable, however, this template will still be editable.",
		);
	}

	setup({ onCellClick, onSettingsButtonClick, hideDrawer, getForecastUrl }) {
		super.setup(...arguments);

		this._onCellClick = onCellClick;
		this._onSettingsButtonClick = onSettingsButtonClick;
		this._hideDrawer = hideDrawer;
		this._getForecastUrl = getForecastUrl;
	}

	handleWidthChange() {
		const viewer = this._forecastTabWidget.getCurrentViewer();
		viewer.adjustReportTable();
	}

	setCurrentTab({ report }) {
		report
			? this._forecastTabWidget.setCurrentTabFromReport(report)
			: this._forecastTabWidget.setCurrentTabFromId(
					forecastSettingsTabId,
				);
	}

	selectCell(...rest) {
		this._forecastTabWidget.selectCell(...rest);
	}

	selectReportAndCell(...rest) {
		this._forecastTabWidget.selectReportAndCell(...rest);
	}

	scrollToSelectedCell() {
		this._forecastTabWidget.scrollToSelectedCell();
	}

	getCurrentReport() {
		return this._forecastTabWidget.getCurrentReport();
	}

	getAllReports() {
		return this._forecastTabWidget.getAllReports();
	}

	updateCurrentReport() {
		this._forecastTabWidget.updateCurrentReport();
	}

	updateSaveSpaceMode() {
		this._forecastTabWidget.updateSaveSpaceMode();
	}

	renderContentOn(html) {
		this._forecastTabWidget = this._makeForecastTabWidget();
		this.setCurrentTab({ report: this._documentState.getCurrentReport() });

		html.render(this._forecastTabWidget);
	}

	_makeForecastTabWidget() {
		const actions = regionWidget({
			content: this._renderActionsOn.bind(this),
		});

		const ruleTemplateSource = this._documentState.getRuleTemplateSource();
		const forecast = this._documentState.getForecast();

		return new ForecastTabsWidget({
			forecast,
			assumptionsWidget: new ForecastAssumptionsWidget({
				forecast,
				ruleTemplateSource,
				onReportAndCellSelectionRequest:
					this.selectReportAndCell.bind(this),
			}),
			extraTabs: [this._makeSettingsTab(), this._makeDescriptionTab()],
			isInSaveSpaceMode: () => this._documentState.isInSaveSpaceMode(),
			onTabClicked: (currentReport) => {
				this._hideDrawer();
				this._documentState.setCurrentReport(currentReport);
				actions.update();
			},
			actions,
			onCellClick: this._onCellClick,
		});
	}

	_makeSettingsTab() {
		return {
			name: _("Forecast Settings"),
			id: forecastSettingsTabId,
			content: new ForecastSettingsWidget({
				accounts: this._documentState.getAllAccounts(),
				configuration: this._documentState.getConfiguration(),
				period: this._documentState.getCurrentPeriod(),
				onPeriodChange: (period) =>
					this._documentState.setCurrentPeriod(period),
				allPeriods: this._fiscalYears.flatMap((year) =>
					year.asMonths(),
				),
			}),
		};
	}

	_makeDescriptionTab() {
		return {
			name: _("Description"),
			id: forecastDescriptionTabId,
			content: this._makeForecastDescriptionEditorRenderer(),
		};
	}

	_makeForecastDescriptionEditorRenderer() {
		const autoSaveDescription = this._makeAutoSaveDebouncer();

		return (html) =>
			html.div(
				{ class: "forecast-description-editor" },
				new WysiwygEditorWidget({
					content: () =>
						this._documentState.getForecastTemplateDescription(),
					onChange: autoSaveDescription,
				}),
			);
	}

	_makeAutoSaveDebouncer() {
		let isLoading = false;

		const autoSaveDebouncer = debounce({
			fn: async (description) => {
				await this._autosaveForecastDescription(description);
				eventManager.at("sync").trigger("loaded");
				isLoading = false;
			},
			delay: 3000,
		});

		return (description) => {
			autoSaveDebouncer(description);
			if (isLoading) return;
			eventManager.at("sync").trigger("loading");
			isLoading = true;
		};
	}

	async _autosaveForecastDescription(description) {
		try {
			await this._documentState.setForecastTemplateDescription(
				description,
			);

			eventManager.at("autoSave").trigger("success");
		} catch {
			eventManager.at("autoSave").trigger("failure");
		}
	}

	_renderActionsOn(html) {
		stacker(
			{ renderOn: html },
			this._renderSettingsButtonOn.bind(this),
			this._renderPublishButtonOn.bind(this),
		);
	}

	_renderSettingsButtonOn(html) {
		if (!this._documentState.getCurrentReport()) {
			return;
		}

		html.button(
			{
				class: "btn",
				click: () =>
					this._onSettingsButtonClick({
						report: this.getCurrentReport(),
					}),
				"data-test": "forecast-display-settings",
			},
			_("Display settings"),
		);
	}

	_renderPublishButtonOn(html) {
		html.button(
			{
				class: "btn btn-primary",
				click: () => this._showNamingForecastDialog(),
				"data-test": "publish",
				"data-help": "publish",
				title: this._forecastHelp,
			},
			_("Publish"),
			" ",
			infoCircleIcon({
				class: "help-button",
			}),
		);
	}

	_showNamingForecastDialog() {
		const templateName = this._documentState.getForecastTemplateName();
		const configuration = this._documentState.getConfiguration();

		const publicationRequest = new ForecastPublicationRequest({
			name: templateName,
			description: configuration.getForecastTemplateDescription(),
		});

		const collector = new AlertCollector();

		this._configure({
			title: (html) =>
				html.span(
					_("Publish"),
					" ",
					infoCircleIcon({
						class: "help-button",
						title: this._forecastHelp,
					}),
				),
			okLabel: _("Publish"),
			waitingOkLabel: _("Publishing…"),
			formSpec: {
				model: publicationRequest,
				saveAction: async () => {
					collector.updateValidationResults({
						model: publicationRequest,
					});

					if (collector.hasError())
						throw new Error("The form isn't valid");

					const forecast = await this._documentState.publishForecast({
						publicationRequest,
					});

					alertBus.success({
						description: _("Forecast published successfully"),
						actions: [
							{
								label: _("Open the published forecast"),
								href: this._getForecastUrl({ forecast }),
							},
						],
					});
				},
			},
			formBuilder: (form) => {
				form.append((html) =>
					html.p(
						_("You're about to publish {templateName}", {
							templateName,
						}),
					),
				);

				form.input({
					accessor: () => publicationRequest.getName(),
					mutator: (value) => publicationRequest.setName(value),
					name: "forecast-name",
					label: _("Forecast name"),
					validator: notBlank(),
				});

				form.wysiwyg({
					accessor: () => publicationRequest.getDescription(),
					mutator: (value) =>
						publicationRequest.setDescription(value),
					label: _("Description"),
					name: "description",
					toolbarAlwaysVisible: true,
				});

				form.group({
					label: _("Include reports"),
					group: "include-reports",
				});

				form.checkbox({
					text: _("Income Statement"),
					name: ForecastIncomeStatement.getType(),
					accessor: () =>
						publicationRequest.isIncomeStatementIncluded(),
					mutator: (value) =>
						publicationRequest.setIncomeStatementInclusion(value),
					group: "include-reports",
				});

				form.checkbox({
					text: _("Balance Sheet"),
					name: ForecastBalanceSheet.getType(),
					accessor: () => publicationRequest.isBalanceSheetIncluded(),
					mutator: (value) =>
						publicationRequest.setBalanceSheetInclusion(value),
					group: "include-reports",
				});

				form.checkbox({
					text: _("Cash Flow"),
					name: ForecastCashFlow.getType(),
					accessor: () => publicationRequest.isCashFlowIncluded(),
					mutator: (value) =>
						publicationRequest.setCashFlowInclusion(value),
					group: "include-reports",
				});

				form.append(new ValidationResultWidget({ collector }));
			},
		});
	}
}
